import React, { useEffect, useState, useCallback, useContext, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { PatientDataContext } from '../../../context/PatientDataContext';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import PreviewModeBanner from '../../../components/PreviewModeBanner';
import { useIsPreviewMode } from '../../../hooks/useIsPreviewMode';
import { usePreviewAwareNavigate } from '../../../hooks/usePreviewAwareNavigate';
import { CheckCircleIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/solid';
import { TriggerGenerateDocumentSection } from '../../../utils/TriggerGenerateDocumentSection';
import { useSectionExitRegeneration } from '../../../hooks/useSectionExitRegeneration';
import { ProformaNavigationGuardContext } from '../../../context/ProformaNavigationGuardContext';
import ConfirmProvisionalSecondaryDiagnoses from '../8_diagnosticDecision/components/ConfirmProvisionalSecondaryDiagnoses';
import { auth } from '../../../firebase';
import { serverTimestamp } from 'firebase/firestore';
import ModuleCompletionDialog, { CancelButton, PrimaryActionButton, GenerateReportButton, DialogMessage } from '../../../components/ModuleCompletionDialog';
import GenerateReportProgressDialog from '../../../components/GenerateReportProgressDialog';
// import { TriggerRenderDocTemplate } from '../../../utils/TriggerRenderDocTemplate';
import { isADHDProvisionalPrimaryDiagnosis } from '../../../utils/conditionChecks';
import { buildFirstGeneratedFields, buildFirstCompletedFields, buildFirstCompletedFieldsDotNotation, appendReportAuditEntry } from '../../../utils/reportAuditHelpers';
import { recordInferredAppointmentDateOnCompletion } from '../../../utils/appointmentDateHelpers';
import { useAuth } from '../../../hooks/useAuth';
import { useCanEditStage } from '../../../hooks/useCanEditStage';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import {
  hasActionableOtherConditions,
  buildDDNavSections,
  getDDRouteForSectionIndex,
  getFlatOtherConditionsStepTitle,
  isAdhdPositiveDecision,
} from './cypDdRouting';
import DDSideNavbar from '../8_diagnosticDecision/components/DDSideNavbar';
import { TriggerSembleMdtReport } from '../../../utils/TriggerSembleMdtReport';
import { resolvePadStatusOnDdComplete } from '../../../utils/padUnlockUtils';

export default function ConfirmSecondaryDiagnoses() {
  const { patientId } = useParams();
  const { patientDataModel, fetchPatientData, updatePatientFirestore, flushPendingUpdates } = useContext(PatientDataContext);
  const { claims } = useAuth();
  // CYP DD is unconditionally multi-step (the 4-stage wizard ending at Final
  // Formulation). Override the adult hasMultiStepDD — which reads from the
  // immutable ddFlow stamp and may be false on older patients — with the
  // CYP-specific cypHasMultiStepDD so every CYP patient gets the wizard.
  const { cypHasMultiStepDD, ...baseWf } = useWorkflowConfig(patientDataModel, claims);
  const wf = { ...baseWf, hasMultiStepDD: cypHasMultiStepDD };

  // Preview mode (?preview=true from the overview's Preview button). The flag
  // forces canEdit=false below even for the assigned DD clinician, and the
  // preview-aware navigate keeps it on every step hop / redirect so the viewer
  // stays read-only across the wizard (Adult Autism DD parity).
  const isPreviewMode = useIsPreviewMode();

  // Determines whether this user is the clinician assigned to the DD stage.
  // When false the entire page renders in read-only mode.
  const canEdit = useCanEditStage(patientDataModel, 'DD', { previewMode: isPreviewMode });

  // Tracks whether the clinician has changed any secondary/comorbid
  // diagnosis decisions or notes. Drives selective regeneration gating
  // and the navigation guard's dirty-state registration.
  const [hasChanges, setHasChanges] = useState(false);

  const navigate = usePreviewAwareNavigate();

  const [isSaving, setIsSaving] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  // When a clinician tries to exit via breadcrumb, top nav, or sidebar
  // while they have unsaved changes, we stash the intended destination
  // here and show the completion dialog. The dialog's "Save & Continue"
  // button then navigates to this route after flushing writes and
  // triggering LLM regeneration.
  const [pendingExitRoute, setPendingExitRoute] = useState(null);

  // False whenever the org skips differentials decisions (cypAdhdFlow, TOC),
  // so the route guard below bounces a direct URL hit back to Decision for
  // ADHD — this page has no work to do for those orgs.
  const hasOtherConditions = useMemo(() => (
    hasActionableOtherConditions({
      patientDataModel,
      isFlatDifferentials: wf.isFlatDifferentials,
      skipDifferentialsDecisions: wf.cypSkipDifferentialsDecisions,
    })
  ), [patientDataModel, wf.isFlatDifferentials, wf.cypSkipDifferentialsDecisions]);

  // DDSideNavbar sections — only built for multi-step DD patients
  // (currentSectionIndex=2, "Decision for Other Conditions" is the active page).
  const ddNavSections = useMemo(() => {
    if (!wf.hasMultiStepDD) return null;
    return buildDDNavSections({
      patientDataModel,
      isFlatDifferentials: wf.isFlatDifferentials,
      isTOCWorkflow: wf.isTOCWorkflow,
      isSLaMWorkflow: wf.isSLaMWorkflow,
      isMDTReview: wf.isMDTReview,
      isSembleMdtReportEnabled: false /* CYP: MDT Plan descoped (22 Aug) */,
      skipDifferentialsDecisions: wf.cypSkipDifferentialsDecisions,
      currentSectionIndex: 2,
    });
  }, [wf.hasMultiStepDD, wf.isTOCWorkflow, wf.isSLaMWorkflow, wf.isMDTReview, patientDataModel, wf.isFlatDifferentials, wf.cypSkipDifferentialsDecisions]);

  // Safety-net hook: triggers AI paragraph regeneration on unmanaged exits
  // (breadcrumb, top nav, browser back) and registers dirty state so the
  // navigation guard can warn before losing unsaved changes.
  const { markRegenHandled } = useSectionExitRegeneration({
    patientId,
    stageTitle: 'CYP ADHD Diagnostic Decision',
    sectionTitle: 'Secondary Diagnoses',
    hasChanges,
    canEdit,
    patientDataModel,
  });

  // ── Exit interceptor ──────────────────────────────────────────────
  // For ALL workflows, breadcrumb / top-nav / sidebar exits with unsaved
  // changes must go through the same save-and-confirm flow as the forward
  // nav button. We register a custom interceptor so that instead of the
  // generic "leaving section" dialog, the clinician sees the full
  // completion dialog showing their decisions before navigation proceeds.
  const { registerExitInterceptor, clearExitInterceptor } = useContext(ProformaNavigationGuardContext);
  const handleFinishDDRef = useRef(null);
  const pendingExitCallbackRef = useRef(null);

  useEffect(() => {
    if (!canEdit) return;

    const interceptor = (destinationUrl, onConfirmCallback) => {
      setPendingExitRoute(destinationUrl);
      pendingExitCallbackRef.current = onConfirmCallback || null;
      if (handleFinishDDRef.current) {
        handleFinishDDRef.current();
      }
    };

    registerExitInterceptor(interceptor);
    return () => clearExitInterceptor();
  }, [canEdit, registerExitInterceptor, clearExitInterceptor]);

  // Whether MDT Plan appears in the sidebar for this patient.
  const ddSection1Data = patientDataModel?.dataModel?.['4_diagnosticDecision']?.sections?.['1'];
  const hasMdtPlan = false /* CYP: MDT Plan descoped (22 Aug) */ && isAdhdPositiveDecision(ddSection1Data);

  // Navigate between DD pages from the sidebar. When the clinician has
  // unsaved changes, the navigation is intercepted: we stash the intended
  // destination, run the same save-and-confirm flow as the forward nav
  // button, and only navigate after the clinician confirms in the dialog.
  // Index→route must omit /other-conditions when that step isn't in the
  // sidebar (see getDDRouteForSectionIndex).
  const navigateToDDSection = useCallback(async (index) => {
    const destination = getDDRouteForSectionIndex(patientId, index, hasOtherConditions, hasMdtPlan);

    if (hasChanges && canEdit && handleFinishDDRef.current) {
      setPendingExitRoute(destination);
      handleFinishDDRef.current();
      return;
    }

    flushPendingUpdates();
    navigate(destination);
  }, [patientId, navigate, flushPendingUpdates, hasChanges, canEdit, hasOtherConditions, hasMdtPlan]);

  // Defensive route guard for stale/inconsistent stage states:
  // when section 2 has no actionable conditions, this page has no meaningful
  // work and can trap users in an empty-state dead-end. We redirect them back
  // to the primary DD page where normal completion options are available.
  useEffect(() => {
    if (hasOtherConditions) return;
    const fallbackRoute = `/patient/${patientId}/proforma/cyp-adhd/diagnostic-decision/${wf.isFlatDifferentials ? 'adhd' : 'primary-diagnosis'}`;
    navigate(fallbackRoute, { replace: true });
  }, [hasOtherConditions, navigate, patientId, wf.isFlatDifferentials]);

  const handleNavigationChoice = async (choice) => {
    if (!canEdit) return;
    document.activeElement?.blur();
    markRegenHandled();
    setIsNavigating(true);

    const dataReady = flushPendingUpdates({ noTimeout: true });

    const generated = patientDataModel?.dataModel?.['6_manageReports']
      ?.sections?.['3']?.stageSectionsGenerated || [];
    const isGenerated = generated.includes('Secondary Diagnoses');
    if (hasChanges || !isGenerated) {
      TriggerGenerateDocumentSection(patientId, 'CYP ADHD Diagnostic Decision', 'Secondary Diagnoses', [], dataReady);
    }

    // TOC: navigate to Formulation page (the final DD step) without running
    // the module-completion transaction — that happens on the Formulation page.
    if (choice === 'formulation') {
      navigate(`/patient/${patientId}/proforma/cyp-adhd/diagnostic-decision/final-formulation`);
      return;
    }

    // Semble MDT Report orgs (WLC): ADHD-positive patients proceed to the MDT
    // Plan page (the final DD step) without running the module-completion
    // transaction — that happens on the MDT Plan page instead.
    if (choice === 'mdtPlan') {
      navigate(`/patient/${patientId}/proforma/cyp-adhd/diagnostic-decision/mdt-plan`);
      return;
    }

    try {
      await dataReady;

      const manageReports = patientDataModel?.dataModel?.['6_manageReports'];
      const ddFirstCompletedFields = buildFirstCompletedFieldsDotNotation('4_diagnosticDecision', patientDataModel?.dataModel?.['4_diagnosticDecision']);
      const firstCompletedFields = buildFirstCompletedFields(patientDataModel?.dataModel?.['4_diagnosticDecision']);

      const padStatusUpdate = resolvePadStatusOnDdComplete(
        patientDataModel,
        shouldShowAdhdFlow || wf.isSSIAlwaysExpected
      );

      // CYP: the adult page's direct dataModel.* transaction is bypassed —
      // there is no monolithic patient dataModel here. All writes go through
      // updatePatientFirestore, which the CYP DD bridge translates into the
      // modular stage documents. (Adult keeps the transaction; this is the
      // one intentional divergence in the copied page.)
      const completionUpdates = {
        '4_diagnosticDecision': {
          status: 'completed',
          progress: 100,
          docs_available: true,
          lastCompletedAt: serverTimestamp(),
          lastCompletedBy: auth.currentUser.uid,
          lastCompletedByName: auth.currentUser.displayName,
          ...firstCompletedFields,
        },
        '6_manageReports': {
          ddReportGenerated: true,
          ddReportGeneratedAt: serverTimestamp(),
          ddReportGeneratedBy: auth.currentUser.uid,
          ddReportGeneratedByName: auth.currentUser.displayName,
          ...buildFirstGeneratedFields('dd', manageReports),
        }
      };
      if (padStatusUpdate) {
        completionUpdates['5_postAssessmentDiscussion'] = { status: padStatusUpdate };
      }
      await updatePatientFirestore(patientId, completionUpdates);

      appendReportAuditEntry(patientId, {
        module: 'dd',
        eventType: 'generated',
        isFirstOccurrence: !manageReports?.ddReportFirstGeneratedAt,
      });

      // Inferred appointment audit on first completion (fire-and-forget; does not write appointmentDates)
      recordInferredAppointmentDateOnCompletion({
        patientId,
        moduleKey: 'dd',
        didWriteFirstCompleted: Object.keys(ddFirstCompletedFields).length > 0,
        enabled: wf.isAppointmentDatesEnabled,
      });

      // Semble MDT Report orgs: this completion path is only reached for
      // non-ADHD outcomes (ADHD-positive patients complete on the MDT Plan
      // page instead). Fire the paragraph generation now that DD is complete
      // so the partial report (Sections 1-2, no Plan) is ready for review.
      if (false /* CYP: MDT Plan descoped (22 Aug) */) {
        TriggerSembleMdtReport({ clinicianId: auth.currentUser.uid, patientId });
      }

      if (choice === 'documents') {
        setIsDialogOpen(false);
        setIsGenerating(true);
      } else if (choice === 'pad') {
        setIsDialogOpen(false);
        navigate(`/patient/${patientId}/proforma/cyp-adhd/post-assessment-discussion/review-assessment`);
      }

    } catch (error) {
      console.error("Error during navigation:", error);
      setIsNavigating(false);
      alert("An error occurred. Please try again.");
    }
  };

  const handleGenerateReport = async () => {
    await handleNavigationChoice('documents');
  };
  
  const handleGoToPostAssessmentDiscussion = async () => {
    await handleNavigationChoice('pad');
  };

  const handleGoToFormulation = async () => {
    await handleNavigationChoice('formulation');
  };

  const handleGoToMdtPlan = async () => {
    await handleNavigationChoice('mdtPlan');
  };

  const handleCancelGeneration = () => {
    setIsGenerating(false);
  };

  const handleNextPageButtonClick = (route) => {
    navigate(route);
  };

  // Track whether the CI Provisional Primary Diagnosis is ADHD (hierarchical model).
  const [isAdhdAfterCI, setIsAdhdAfterCI] = useState(true);

  useEffect(() => {
    const useAdhdFlow = isADHDProvisionalPrimaryDiagnosis(patientDataModel);
    console.log('Using ADHD Flow:', useAdhdFlow)
    setIsAdhdAfterCI(useAdhdFlow);
  }, [patientDataModel]);

  // Flat-model and deferred-DF orgs always show the ADHD DD flow (and
  // therefore always need PAD unlocked) even when isAdhdAfterCI is false
  // because there is no PPD concept.  This mirrors 1_DD.js.
  const shouldShowAdhdFlow = isAdhdAfterCI || wf.isDFDeferred || wf.isFlatDifferentials;

  // Semble MDT Report orgs (Well Life Clinic): when the primary ADHD decision
  // (recorded on 1_DD, stored in DD section 1) was positive, the module does
  // NOT complete on this page — the clinician proceeds to the MDT Plan page,
  // the deferred final DD step for the Semble MDT Report. Non-ADHD outcomes
  // complete here as normal (partial Semble report, Sections 1-2 only).
  const goesToMdtPlan = false /* CYP: MDT Plan descoped (22 Aug) */
    && isAdhdPositiveDecision(patientDataModel?.dataModel?.['4_diagnosticDecision']?.sections?.['1']);

  // Whether to show the "Go to Post-Assessment Discussion" button.
  // PAD is part of the workflow when the ADHD criteria panel was shown
  // (shouldShowAdhdFlow) OR the org always conducts a full assessment.
  // wf.isMDTReview determines which action button is visually emphasised
  // (primary) in the completion dialog — MDT Review orgs default to
  // "Generate Report" as primary; others default to "Go to PAD".
  const shouldShowPADButton = useCallback(() => {
    return shouldShowAdhdFlow || wf.isSSIAlwaysExpected;
  }, [shouldShowAdhdFlow, wf.isSSIAlwaysExpected]);
  
  const handleFinishDD = useCallback(async () => {
    if (!canEdit || isSaving) return;
    document.activeElement?.blur();
    await flushPendingUpdates();
    setIsSaving(true);
    
    try {
      // Refresh patient data first
      await fetchPatientData(patientId);
      setIsSaving(false);

      // ALWAYS show dialog - all navigation logic will be handled in dialog buttons
      setIsDialogOpen(true);
      
    } catch (error) {
      console.error("Failed to refresh patient data:", error);
      setIsSaving(false);
      setPendingExitRoute(null);
      pendingExitCallbackRef.current = null;
    }
  }, [canEdit, isSaving, patientId, fetchPatientData, flushPendingUpdates]);

  // Keep the ref in sync so the exit interceptor can call handleFinishDD
  // without a stale closure.
  handleFinishDDRef.current = handleFinishDD;

  // TOC shortcut: proceed directly to Formulation without a confirmation
  // dialog — the module completion transaction happens on that page instead.
  const handleProceedToFormulation = useCallback(async () => {
    if (!canEdit) return;
    await flushPendingUpdates();
    setIsSaving(true);
    await handleNavigationChoice('formulation');
  }, [canEdit, flushPendingUpdates, handleNavigationChoice]);

  // "Save & Continue" handler for intercepted exits (breadcrumb, top nav,
  // sidebar). Flushes pending writes, triggers LLM regeneration for
  // Secondary Diagnoses, marks the regen as handled (so the unmount
  // cleanup doesn't double-fire), and navigates to the original destination.
  const handleSaveAndContinue = useCallback(async () => {
    if (!pendingExitRoute) return;
    setIsNavigating(true);
    markRegenHandled();

    const dataReady = flushPendingUpdates();

    const generated = patientDataModel?.dataModel?.['6_manageReports']
      ?.sections?.['3']?.stageSectionsGenerated || [];
    const isGenerated = generated.includes('Secondary Diagnoses');
    if (hasChanges || !isGenerated) {
      TriggerGenerateDocumentSection(patientId, 'CYP ADHD Diagnostic Decision', 'Secondary Diagnoses', [], dataReady);
    }

    const destination = pendingExitRoute;
    const exitCallback = pendingExitCallbackRef.current;
    setPendingExitRoute(null);
    pendingExitCallbackRef.current = null;
    setIsDialogOpen(false);

    if (exitCallback) {
      await exitCallback();
    } else {
      navigate(destination);
    }
  }, [pendingExitRoute, patientId, patientDataModel, hasChanges, navigate, flushPendingUpdates, markRegenHandled]);

  const flatStepTitle = wf.isFlatDifferentials
    ? getFlatOtherConditionsStepTitle(wf.isTOCWorkflow)
    : null;

  const breadcrumbTuples = [
    ["Patient Overview", `/patient/${patientId}/cyp-adhd`],
    [flatStepTitle || 'Diagnostic Decision'],
  ];

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />
      {/* Multi-step DD patients get a sidebar navigation; all other patients
          keep the existing single-column layout with no structural changes. */}
      <div className={wf.hasMultiStepDD ? 'flex items-start' : ''}>
        {wf.hasMultiStepDD && ddNavSections && (
          <DDSideNavbar
            sections={ddNavSections}
            currentSectionIndex={2}
            navigateToSection={navigateToDDSection}
          />
        )}
      <div className="flex-grow p-8">
      {/* Preview mode info banner — shown when opened via a Preview button */}
      {isPreviewMode && <PreviewModeBanner />}
      <div className="flex justify-between items-center mb-3">

          {/* Back button */}
          <button
            onClick={() => {
              const backRoute = `/patient/${patientId}/proforma/cyp-adhd/diagnostic-decision/${wf.isFlatDifferentials ? 'adhd' : 'primary-diagnosis'}`;
              if (hasChanges && canEdit && handleFinishDDRef.current) {
                setPendingExitRoute(backRoute);
                handleFinishDDRef.current();
                return;
              }
              flushPendingUpdates();
              navigate(backRoute);
            }}
            className="flex items-center py-2 px-4 mb-2 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300"
          >
            <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />
            <span className="font-bold">{wf.isFlatDifferentials ? "Back to Decision for ADHD" : "Back to Primary Diagnosis"}</span>
          </button>

          {/* Centered heading */}
          <div className="flex items-center">
            <h1 className="text-2xl font-bold">
              {flatStepTitle || 'Secondary and/or Comorbid Diagnoses'}
            </h1>
          </div>

          {/* Forward button — read-only users see navigation-only variant */}
          {canEdit ? (
            <button
              onClick={wf.hasMultiStepDD ? handleProceedToFormulation : handleFinishDD}
              disabled={!isFormValid || isSaving}
              className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition-all duration-300 ${
                isFormValid && !isSaving ? 'hover:bg-violet-800 transition duration-100 bg-violet-900 text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isSaving ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="font-bold">{wf.hasMultiStepDD ? 'Saving...' : 'Saving Diagnostic Decision...'}</span>
                </>
              ) : (
                <>
                  <span className="font-bold">{wf.hasMultiStepDD ? 'Proceed to Formulation' : 'Complete Diagnostic Decision'}</span>
                  {wf.hasMultiStepDD
                    ? <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />
                    : <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />
                  }
                </>
              )}
            </button>
          ) : wf.hasMultiStepDD && (
            <button
              onClick={() => navigate(`/patient/${patientId}/proforma/cyp-adhd/diagnostic-decision/final-formulation`)}
              className="flex items-center py-2 px-4 rounded-md shadow-md bg-violet-900 hover:bg-violet-800 text-white font-bold transition-all duration-300"
            >
              <span>Next: Formulation</span>
              <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />
            </button>
          )}
        </div>

        {/* Display the Secondary Diagnoses component */}        
        <ConfirmProvisionalSecondaryDiagnoses setFormValid={setIsFormValid} canEdit={canEdit} onDirty={() => setHasChanges(true)} />
        
        <div className="flex justify-between items-center mt-8 pt-4 border-t border-gray-200">
          <div className="w-1/3 flex justify-start">
            <button
              onClick={() => {
                const backRoute = `/patient/${patientId}/proforma/cyp-adhd/diagnostic-decision/${wf.isFlatDifferentials ? 'adhd' : 'primary-diagnosis'}`;
                if (hasChanges && canEdit && handleFinishDDRef.current) {
                  setPendingExitRoute(backRoute);
                  handleFinishDDRef.current();
                  return;
                }
                flushPendingUpdates();
                navigate(backRoute);
              }}
              className="flex items-center py-2 px-4 mb-2 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300 hover:bg-gray-50"
            >
              <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />
              <span className="font-bold">{wf.isFlatDifferentials ? "Back to Decision for ADHD" : "Back to Primary Diagnosis"}</span>
            </button>
          </div>

          <div className="w-1/3" />

          <div className="w-1/3 flex justify-end">
          {canEdit ? (
            <button
              onClick={wf.hasMultiStepDD ? handleProceedToFormulation : handleFinishDD}
              disabled={!isFormValid || isSaving}
              className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition-all duration-300 ${
                isFormValid && !isSaving ? 'hover:bg-violet-800 transition duration-100 bg-violet-900 text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isSaving ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="font-bold">{wf.hasMultiStepDD ? 'Saving...' : 'Saving Diagnostic Decision...'}</span>
                </>
              ) : (
                <>
                  <span className="font-bold">{wf.hasMultiStepDD ? 'Proceed to Formulation' : 'Complete Diagnostic Decision'}</span>
                  {wf.hasMultiStepDD
                    ? <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />
                    : <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />
                  }
                </>
              )}
            </button>
          ) : wf.hasMultiStepDD && (
            <button
              onClick={() => navigate(`/patient/${patientId}/proforma/cyp-adhd/diagnostic-decision/final-formulation`)}
              className="flex items-center py-2 px-4 rounded-md shadow-md bg-violet-900 hover:bg-violet-800 text-white font-bold transition-all duration-300"
            >
              <span>Next: Formulation</span>
              <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />
            </button>
          )}
          </div>
        </div>

      </div>
      </div>{/* closes the flex items-start wrapper (TOC sidebar layout) */}

      {/* Module completion dialog for Secondary/Comorbid Diagnoses.
          Same MDT-aware button ordering as the primary DD dialog. */}
      <ModuleCompletionDialog
        open={isDialogOpen && !isGenerating}
        onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) { setPendingExitRoute(null); pendingExitCallbackRef.current = null; }
        }}
        title={goesToMdtPlan ? "Diagnostic Decisions Recorded" : "Diagnostic Decision Complete"}
        description={goesToMdtPlan
          ? "You have recorded diagnostic decisions for all conditions. The next step is to complete the MDT Plan."
          : wf.isFlatDifferentials
            ? "You have completed diagnostic decisions for all conditions."
            : <>You have completed the <span className="font-semibold">Diagnostic Decision</span> module including secondary and comorbid diagnoses.</>}
        actions={(() => {
          const showBothOptions = shouldShowPADButton();

          // Intercepted exit: the clinician was trying to leave via
          // breadcrumb, top nav, or sidebar. Show a single "Save &
          // Continue" button that triggers LLM regen and navigates to
          // wherever they were originally headed.
          if (pendingExitRoute) {
            return (
              <>
                <CancelButton onClick={() => { setPendingExitRoute(null); pendingExitCallbackRef.current = null; setIsDialogOpen(false); }} />
                <PrimaryActionButton
                  key="save-continue"
                  label={isNavigating ? "Saving..." : "Save & Continue"}
                  onClick={() => { if (!isNavigating) handleSaveAndContinue(); }}
                  disabled={isNavigating}
                />
              </>
            );
          }

          const generateReportBtn = (
            <GenerateReportButton
              key="generate"
              onClick={() => { if (!isNavigating) handleGenerateReport(); }}
              disabled={isNavigating}
              secondary={showBothOptions && !wf.isMDTReview}
              savingLabel={isNavigating ? "Saving..." : undefined}
            />
          );

          const padBtn = shouldShowPADButton() && (
            <PrimaryActionButton
              key="pad"
              label={isNavigating ? "Saving..." : "Go to Post-Assessment Discussion"}
              onClick={() => { if (!isNavigating) handleGoToPostAssessmentDiscussion(); }}
              disabled={isNavigating}
              secondary={wf.isMDTReview}
            />
          );

          // Multi-step DD patients proceed to Formulation (the final DD step)
          // rather than completing the module here.
          const formulationBtn = wf.hasMultiStepDD && (
            <PrimaryActionButton
              key="formulation"
              label={isNavigating ? "Saving..." : "Go to Formulation"}
              onClick={() => { if (!isNavigating) handleGoToFormulation(); }}
              disabled={isNavigating}
            />
          );

          // Semble MDT Report orgs (WLC): ADHD-positive patients proceed to
          // the MDT Plan page (the final DD step) rather than completing the
          // module here.
          const mdtPlanBtn = goesToMdtPlan && (
            <PrimaryActionButton
              key="mdt-plan"
              label={isNavigating ? "Saving..." : "Go to MDT Plan"}
              onClick={() => { if (!isNavigating) handleGoToMdtPlan(); }}
              disabled={isNavigating}
            />
          );

          return (
            <>
              <CancelButton onClick={() => setIsDialogOpen(false)} />
              {wf.hasMultiStepDD
                ? formulationBtn
                : goesToMdtPlan
                  ? mdtPlanBtn
                  : wf.isMDTReview
                    ? <>{padBtn}{generateReportBtn}</>
                    : <>{generateReportBtn}{padBtn}</>
              }
            </>
          );
        })()}
      >
        <DialogMessage>Are you happy to proceed?</DialogMessage>
      </ModuleCompletionDialog>

      <GenerateReportProgressDialog
        reportStage="Diagnostic Decision"
        patientId={patientId}
        clinicianId={auth.currentUser?.uid}
        handleNextPageButtonClick={() => handleNextPageButtonClick(`/patient/${patientId}/cyp-adhd/documents`)}
        isGenerating={isGenerating}
        onCancel={handleCancelGeneration}
      />
    </Layout>
  );
}